import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per hockey match
 * {
 *   matchType: "regulation" | "overtime" | "shootout",
 *   periodLength: number,
 *   team1Name: string,
 *   team2Name: string,
 *   team1Players: [{ id, userId, name, position, isCustom }],
 *   team2Players: [{ id, userId, name, position, isCustom }],
 *   periods: [{ number, team1Goals, team2Goals }],
 *   overtime: { team1Goals, team2Goals, duration },
 *   shootout: { team1Goals, team2Goals, attempts: [] },
 *   goalEvents: [
 *     { id, scorer, scorerId, assist, assistId, team: "team1" | "team2", period, time, type }
 *   ],
 *   penaltyEvents: [
 *     { id, player, playerId, team: "team1" | "team2", infraction, minutes, period, time, isGameMisconduct }
 *   ],
 *   winner: "team1" | "team2" | "tie",
 *   finalScore: { team1: number, team2: number }
 * }
 */

const brandBlue = "#3498db";
const brandBlueDark = "#2980b9";

export const AddHockey = () => {
  const location = useLocation();
  const data = location.state || {};
  const navigate = useNavigate();

  const [hockeyMatches, setHockeyMatches] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  const validateMatches = () => {
    const errors = [];

    hockeyMatches.forEach((match, index) => {
      // Check team names
      if (!match.team1Name || !match.team2Name) {
        errors.push(`Match ${index + 1}: Team names are required`);
      }

      // Check minimum players (at least 3 for practice - 2 field + 1 goalie)
      if (match.team1Players.length < 3 || match.team2Players.length < 3) {
        errors.push(
          `Match ${
            index + 1
          }: Each team must have at least 3 players (including goalie)`
        );
      }

      // Check maximum players (6 for ice hockey)
      if (match.team1Players.length > 6 || match.team2Players.length > 6) {
        errors.push(
          `Match ${index + 1}: Each team cannot have more than 6 players`
        );
      }

      // Check if teams have equal number of players
      if (match.team1Players.length !== match.team2Players.length) {
        errors.push(
          `Match ${index + 1}: Both teams must have equal number of players`
        );
      }

      // Check if match has any scoring data
      const hasGoalEvents = match.goalEvents.length > 0;
      const hasPeriodGoals = match.periods.some(
        (period) => period.team1Goals > 0 || period.team2Goals > 0
      );

      if (!hasGoalEvents && !hasPeriodGoals) {
        errors.push(
          `Match ${
            index + 1
          }: Match must have at least one goal scored or goal event recorded`
        );
      }

      // Validate goal events
      match.goalEvents.forEach((goal, goalIndex) => {
        if (!goal.scorer || !goal.period || !goal.time) {
          errors.push(
            `Match ${index + 1}, Goal ${
              goalIndex + 1
            }: Scorer, period, and time are required`
          );
        }

        // Check if period is valid
        if (goal.period < 1 || goal.period > 3) {
          errors.push(
            `Match ${index + 1}, Goal ${
              goalIndex + 1
            }: Period must be between 1 and 3`
          );
        }

        // Check if time format is valid (MM:SS)
        const timePattern = /^([0-1]?[0-9]|20):[0-5][0-9]$/;
        if (!timePattern.test(goal.time)) {
          errors.push(
            `Match ${index + 1}, Goal ${
              goalIndex + 1
            }: Time must be in MM:SS format (max 20:00)`
          );
        }
      });

      // Validate penalty events
      match.penaltyEvents.forEach((penalty, penIndex) => {
        if (
          !penalty.player ||
          !penalty.infraction ||
          !penalty.period ||
          !penalty.time
        ) {
          errors.push(
            `Match ${index + 1}, Penalty ${
              penIndex + 1
            }: Player, infraction, period, and time are required`
          );
        }

        if (!penalty.minutes || penalty.minutes < 1 || penalty.minutes > 10) {
          errors.push(
            `Match ${index + 1}, Penalty ${
              penIndex + 1
            }: Penalty minutes must be between 1 and 10`
          );
        }
      });

      // Check if match has a definitive result
      if (match.winner === null || match.winner === undefined) {
        if (match.finalScore.team1 !== match.finalScore.team2) {
          errors.push(
            `Match ${
              index + 1
            }: Match has a score difference but no winner declared`
          );
        }
      }

      // Validate final scores match the goal events
      const calculatedTeam1Goals =
        match.goalEvents.filter((g) => g.team === "team1").length +
        match.overtime.team1Goals +
        match.shootout.team1Goals;
      const calculatedTeam2Goals =
        match.goalEvents.filter((g) => g.team === "team2").length +
        match.overtime.team2Goals +
        match.shootout.team2Goals;

      if (
        calculatedTeam1Goals !== match.finalScore.team1 ||
        calculatedTeam2Goals !== match.finalScore.team2
      ) {
        errors.push(
          `Match ${
            index + 1
          }: Final scores don't match recorded goals. Calculated: ${calculatedTeam1Goals}-${calculatedTeam2Goals}, Final: ${
            match.finalScore.team1
          }-${match.finalScore.team2}`
        );
      }
    });

    return errors;
  };

  const handleSaveAll = async () => {
    const validationErrors = validateMatches();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following errors:\n\n" + validationErrors.join("\n")
      );
      return;
    }

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: hockeyMatches,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      const responseData = await response.json();
      toast.success("Saved all hockey matches!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  const handleAddHockeyMatch = () => {
    const newMatch = {
      matchType: "regulation", // regulation, overtime, shootout
      periodLength: 20, // minutes per period
      team1Name: "",
      team2Name: "",
      team1Players: [],
      team2Players: [],
      periods: [
        { number: 1, team1Goals: 0, team2Goals: 0 },
        { number: 2, team1Goals: 0, team2Goals: 0 },
        { number: 3, team1Goals: 0, team2Goals: 0 },
      ],
      overtime: { team1Goals: 0, team2Goals: 0, duration: 0 },
      shootout: { team1Goals: 0, team2Goals: 0, attempts: [] },
      goalEvents: [], // {id, scorer, scorerId, assist, assistId, team, period, time, type: "even", "powerplay", "shorthanded", "penalty-shot"}
      penaltyEvents: [], // {id, player, playerId, team, infraction, minutes, period, time, isGameMisconduct}
      winner: null, // team1, team2, or tie
      finalScore: { team1: 0, team2: 0 },
    };
    setHockeyMatches((prev) => [...prev, newMatch]);
  };

  const handleRemoveMatch = (index) => {
    setHockeyMatches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMatch = (index, updatedMatch) => {
    setHockeyMatches((prevMatches) =>
      prevMatches.map((m, i) => (i === index ? updatedMatch : m))
    );
  };

  // Tailwind class helpers for consistent styling
  const brandBlue = "#3498db";
  const brandBlueDark = "#2980b9";

  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    noMatchesMessage:
      "text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg",
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />

      <div className={classes.container}>
        <div className={classes.header} style={{ borderColor: brandBlue }}>
          <h2 className={classes.headerTitle}>
            {data.sport} Matches at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time ? (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            ) : (
              "Add detailed hockey match information"
            )}
          </p>
        </div>

        <button
          onClick={handleAddHockeyMatch}
          className={classes.addButton}
        >
          Add New Hockey Match
        </button>

        {hockeyMatches.length === 0 && (
          <div className={classes.noMatchesMessage}>
            No matches added yet. Click the button above to add your first
            hockey match.
          </div>
        )}

        {hockeyMatches.map((match, index) => (
          <HockeyMatchForm
            key={index}
            match={match}
            matchNumber={index + 1}
            availableUsers={availableUsers}
            onUpdate={(updatedMatch) => handleUpdateMatch(index, updatedMatch)}
            onRemove={() => handleRemoveMatch(index)}
          />
        ))}

        {hockeyMatches.length > 0 && (
          <button onClick={handleSaveAll} className={classes.saveButton}>
            Save All Matches
          </button>
        )}
      </div>
    </>
  );
};

const HockeyMatchForm = ({
  match,
  matchNumber,
  availableUsers,
  onUpdate,
  onRemove,
}) => {
  const [localMatch, setLocalMatch] = useState(match);
  const [activeTab, setActiveTab] = useState("basic"); // basic, teams, events, score

  const formClasses = {
    matchCard:
      "border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 shadow-sm",
    matchHeader:
      "flex justify-between items-start md:items-center flex-col md:flex-row gap-3 border-b border-gray-200 pb-4 mb-5",
    matchTitle: "text-xl font-semibold text-gray-800",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    tabContainer: "flex mb-5 border-b border-gray-200 flex-wrap",
    tab: "py-2 px-4 text-sm sm:text-base font-medium border-b-2 border-transparent bg-transparent transition-colors",
    activeTab: "",
    formGroup: "space-y-4",
    label: "font-medium text-gray-700",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    select:
      "px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-3 py-2 rounded-lg transition-colors duration-200",
    teamSection: "bg-white p-5 rounded-lg shadow-sm mb-5",
    playerCard:
      "bg-gray-50 p-3 rounded-lg mb-3 flex justify-between items-center",
    deleteButton: "bg-red-500 text-white px-2 py-1 rounded text-xs",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
  };

  const updateParent = (updated) => {
    setLocalMatch(updated);
    onUpdate(updated);
  };

  const tabBaseStyle = {
    padding: "10px 16px",
    fontWeight: 600,
    fontSize: "16px",
    color: "#718096",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const tabActiveStyle = {
    color: brandBlue,
    borderBottomColor: brandBlue,
  };

  const addPlayer = (team, userInfo, customName = "", position = "forward") => {
    const updated = { ...localMatch };
    const player = {
      id: userInfo?.id || `custom_${Date.now()}`,
      userId: userInfo?.id || null,
      name: userInfo?.full_name || customName || "Guest Player",
      position: position, // forward, defense, goalie
      isCustom: !userInfo,
    };

    updated[`${team}Players`].push(player);
    updateParent(updated);
  };

  const removePlayer = (team, playerIndex) => {
    const updated = { ...localMatch };
    updated[`${team}Players`].splice(playerIndex, 1);
    updateParent(updated);
  };

  const addGoalEvent = () => {
    const updated = { ...localMatch };
    updated.goalEvents.push({
      id: Date.now(),
      scorer: "",
      scorerId: null,
      assist: "",
      assistId: null,
      team: "team1",
      period: 1,
      time: "00:00",
      type: "even", // even, powerplay, shorthanded, penalty-shot
    });
    updateParent(updated);
  };

  const updateGoalEvent = (index, field, value) => {
    const updated = { ...localMatch };
    updated.goalEvents[index][field] = value;

    // If updating scorer, also update scorerId
    if (field === "scorer" && value) {
      const team = updated.goalEvents[index].team;
      const selectedPlayer = updated[`${team}Players`].find(
        (player) => player.name === value
      );
      updated.goalEvents[index].scorerId = selectedPlayer?.userId || null;
    }

    // If updating assist, also update assistId
    if (field === "assist" && value) {
      const team = updated.goalEvents[index].team;
      const selectedPlayer = updated[`${team}Players`].find(
        (player) => player.name === value
      );
      updated.goalEvents[index].assistId = selectedPlayer?.userId || null;
    }

    recalculateScore(updated);
  };

  const removeGoalEvent = (index) => {
    const updated = { ...localMatch };
    updated.goalEvents.splice(index, 1);
    recalculateScore(updated);
  };

  const addPenaltyEvent = () => {
    const updated = { ...localMatch };
    updated.penaltyEvents.push({
      id: Date.now(),
      player: "",
      playerId: null,
      team: "team1",
      infraction: "",
      minutes: 2,
      period: 1,
      time: "00:00",
      isGameMisconduct: false,
    });
    updateParent(updated);
  };

  const updatePenaltyEvent = (index, field, value) => {
    const updated = { ...localMatch };
    updated.penaltyEvents[index][field] = value;

    // If updating player, also update playerId
    if (field === "player" && value) {
      const team = updated.penaltyEvents[index].team;
      const selectedPlayer = updated[`${team}Players`].find(
        (player) => player.name === value
      );
      updated.penaltyEvents[index].playerId = selectedPlayer?.userId || null;
    }

    updateParent(updated);
  };

  const removePenaltyEvent = (index) => {
    const updated = { ...localMatch };
    updated.penaltyEvents.splice(index, 1);
    updateParent(updated);
  };

  const recalculateScore = (updatedMatch) => {
    let team1Total = 0;
    let team2Total = 0;

    // Count goals from events
    updatedMatch.goalEvents.forEach((goal) => {
      if (goal.team === "team1") team1Total++;
      else team2Total++;
    });

    // Add overtime goals
    team1Total += updatedMatch.overtime.team1Goals;
    team2Total += updatedMatch.overtime.team2Goals;

    // Add shootout goals
    team1Total += updatedMatch.shootout.team1Goals;
    team2Total += updatedMatch.shootout.team2Goals;

    updatedMatch.finalScore = { team1: team1Total, team2: team2Total };

    // Determine winner
    if (team1Total > team2Total) {
      updatedMatch.winner = "team1";
    } else if (team2Total > team1Total) {
      updatedMatch.winner = "team2";
    } else {
      updatedMatch.winner = "tie";
    }

    // Update period scores
    for (let i = 1; i <= 3; i++) {
      const periodGoals1 = updatedMatch.goalEvents.filter(
        (g) => g.team === "team1" && g.period === i
      ).length;
      const periodGoals2 = updatedMatch.goalEvents.filter(
        (g) => g.team === "team2" && g.period === i
      ).length;

      const period = updatedMatch.periods.find((p) => p.number === i);
      if (period) {
        period.team1Goals = periodGoals1;
        period.team2Goals = periodGoals2;
      }
    }

    updateParent(updatedMatch);
  };

  const renderBasicInfo = () => (
    <div>
      {/* Hockey Match Guidance */}
      <div className={formClasses.guidanceBox}>
        <h4 className={formClasses.guidanceTitle}>
          🏒 Hockey Match Guide
        </h4>
        <div className={formClasses.guidanceContent}>
          <p>
            <strong>Team Size:</strong> Standard 6 players (1 goalie + 5 field
            players)
          </p>
          <p>
            <strong>Periods:</strong> 3 periods of 20 minutes each
          </p>
          <p>
            <strong>Positions:</strong> Goalie, Defense (2), Forward (3) - but
            flexible for smaller teams
          </p>
          <p>
            <strong>Scoring:</strong> Goals and assists tracked with time and
            period
          </p>
          <p>
            <strong>Penalties:</strong> Minor (2 min), Major (5 min), Game
            Misconduct (10 min)
          </p>
          <p>
            <strong>Special Situations:</strong> Power play, short-handed,
            penalty shots
          </p>
          <p>
            <strong>Tip:</strong> Add goal events with time stamps for detailed
            tracking!
          </p>
        </div>
      </div>

      <div className={formClasses.formGroup}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Team 1 Name</label>
            <input
              type="text"
              value={localMatch.team1Name}
              onChange={(e) =>
                updateParent({ ...localMatch, team1Name: e.target.value })
              }
              className={formClasses.input}
              placeholder="Enter team 1 name"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Team 2 Name</label>
            <input
              type="text"
              value={localMatch.team2Name}
              onChange={(e) =>
                updateParent({ ...localMatch, team2Name: e.target.value })
              }
              className={formClasses.input}
              placeholder="Enter team 2 name"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 mt-3">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Match Type</label>
            <select
              value={localMatch.matchType}
              onChange={(e) =>
                updateParent({ ...localMatch, matchType: e.target.value })
              }
              className={`${formClasses.select} w-full`}
            >
              <option value="regulation">Regulation (3 periods)</option>
              <option value="overtime">With Overtime</option>
              <option value="shootout">With Shootout</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Period Length (minutes)</label>
            <input
              type="number"
              value={localMatch.periodLength}
              onChange={(e) => {
                const length = Math.max(5, Math.min(20, Number(e.target.value)));
                updateParent({ ...localMatch, periodLength: length });
              }}
              className={formClasses.input}
              min="5"
              max="20"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeamManagement = () => (
    <div>
      {/* Team 1 */}
      <div className={formClasses.teamSection}>
        <h4 className="text-lg font-semibold text-gray-800 mb-2">
          {localMatch.team1Name || "Team 1"} ({localMatch.team1Players.length}{" "}
          players)
        </h4>
        <PlayerSelector
          availableUsers={availableUsers}
          onAddPlayer={(userInfo, customName, position) =>
            addPlayer("team1", userInfo, customName, position)
          }
          selectedUserIds={[
            ...localMatch.team1Players
              .filter((p) => p.userId)
              .map((p) => p.userId),
            ...localMatch.team2Players
              .filter((p) => p.userId)
              .map((p) => p.userId),
          ]}
        />
        <div className="mt-4 space-y-3">
          {localMatch.team1Players.map((player, index) => (
            <div key={player.id} className={formClasses.playerCard}>
              <div className="flex items-center space-x-4">
                <span className="font-medium text-gray-800">
                  {index + 1}. {player.name} {player.isCustom && "(Custom)"}
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    player.position === "goalie"
                      ? "bg-yellow-100 text-yellow-800"
                      : player.position === "defense"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {player.position}
                </span>
              </div>
              <button
                onClick={() => removePlayer("team1", index)}
                className={formClasses.deleteButton}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Team 2 */}
      <div className={formClasses.teamSection}>
        <h4 className="text-lg font-semibold text-gray-800 mb-2">
          {localMatch.team2Name || "Team 2"} ({localMatch.team2Players.length}{" "}
          players)
        </h4>
        <PlayerSelector
          availableUsers={availableUsers}
          onAddPlayer={(userInfo, customName, position) =>
            addPlayer("team2", userInfo, customName, position)
          }
          selectedUserIds={[
            ...localMatch.team1Players
              .filter((p) => p.userId)
              .map((p) => p.userId),
            ...localMatch.team2Players
              .filter((p) => p.userId)
              .map((p) => p.userId),
          ]}
        />
        <div className="mt-4 space-y-3">
          {localMatch.team2Players.map((player, index) => (
            <div key={player.id} className={formClasses.playerCard}>
              <div className="flex items-center space-x-4">
                <span className="font-medium text-gray-800">
                  {index + 1}. {player.name} {player.isCustom && "(Custom)"}
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    player.position === "goalie"
                      ? "bg-yellow-100 text-yellow-800"
                      : player.position === "defense"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {player.position}
                </span>
              </div>
              <button
                onClick={() => removePlayer("team2", index)}
                className={formClasses.deleteButton}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Team Balance Warning */}
      {localMatch.team1Players.length !== localMatch.team2Players.length && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            ⚠️ Both teams should have equal number of players. Currently Team 1
            has {localMatch.team1Players.length} and Team 2 has{" "}
            {localMatch.team2Players.length} players.
          </p>
        </div>
      )}
    </div>
  );

  const renderEvents = () => (
    <div>
      {/* Goals Section */}
      <div className={formClasses.teamSection}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">Goals</h4>
          <button
            onClick={addGoalEvent}
            className={formClasses.addButton}
          >
            Add Goal
          </button>
        </div>

        <div className="space-y-4">
          {localMatch.goalEvents.map((goal, index) => (
            <div
              key={goal.id}
              className="bg-white p-4 rounded-lg mb-3 border border-gray-200 grid gap-3 items-center grid-cols-1 sm:grid-cols-7"
            >
              <select
                value={goal.team}
                onChange={(e) => updateGoalEvent(index, "team", e.target.value)}
                className={formClasses.select}
              >
                <option value="team1">
                  {localMatch.team1Name || "Team 1"}
                </option>
                <option value="team2">
                  {localMatch.team2Name || "Team 2"}
                </option>
              </select>

              <select
                value={goal.scorer}
                onChange={(e) =>
                  updateGoalEvent(index, "scorer", e.target.value)
                }
                className={formClasses.input}
              >
                <option value="">Select scorer</option>
                {localMatch[`${goal.team}Players`].map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))}
              </select>

              <select
                value={goal.assist}
                onChange={(e) =>
                  updateGoalEvent(index, "assist", e.target.value)
                }
                className={formClasses.input}
              >
                <option value="">Assist (optional)</option>
                {localMatch[`${goal.team}Players`].map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))}
              </select>

              <select
                value={goal.period}
                onChange={(e) =>
                  updateGoalEvent(index, "period", Number(e.target.value))
                }
                className={formClasses.select}
              >
                <option value={1}>Period 1</option>
                <option value={2}>Period 2</option>
                <option value={3}>Period 3</option>
              </select>

              <input
                type="text"
                value={goal.time}
                onChange={(e) => updateGoalEvent(index, "time", e.target.value)}
                placeholder="MM:SS"
                className={formClasses.input}
              />

              <select
                value={goal.type}
                onChange={(e) => updateGoalEvent(index, "type", e.target.value)}
                className={formClasses.select}
              >
                <option value="even">Even Strength</option>
                <option value="powerplay">Power Play</option>
                <option value="shorthanded">Short Handed</option>
                <option value="penalty-shot">Penalty Shot</option>
              </select>

              <button
                onClick={() => removeGoalEvent(index)}
                className={formClasses.deleteButton}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Penalties Section */}
      <div className={formClasses.teamSection}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">Penalties</h4>
          <button
            onClick={addPenaltyEvent}
            className={formClasses.addButton}
          >
            Add Penalty
          </button>
        </div>

        <div className="space-y-4">
          {localMatch.penaltyEvents.map((penalty, index) => (
            <div
              key={penalty.id}
              className="bg-white p-4 rounded-lg mb-3 border border-gray-200 grid gap-3 items-center grid-cols-1 sm:grid-cols-7"
            >
              <select
                value={penalty.team}
                onChange={(e) =>
                  updatePenaltyEvent(index, "team", e.target.value)
                }
                className={formClasses.select}
              >
                <option value="team1">
                  {localMatch.team1Name || "Team 1"}
                </option>
                <option value="team2">
                  {localMatch.team2Name || "Team 2"}
                </option>
              </select>

              <select
                value={penalty.player}
                onChange={(e) =>
                  updatePenaltyEvent(index, "player", e.target.value)
                }
                className={formClasses.input}
              >
                <option value="">Select player</option>
                {localMatch[`${penalty.team}Players`].map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={penalty.infraction}
                onChange={(e) =>
                  updatePenaltyEvent(index, "infraction", e.target.value)
                }
                placeholder="Infraction (e.g., Tripping)"
                className={formClasses.input}
              />

              <select
                value={penalty.minutes}
                onChange={(e) =>
                  updatePenaltyEvent(index, "minutes", Number(e.target.value))
                }
                className={formClasses.select}
              >
                <option value={2}>2 min (Minor)</option>
                <option value={4}>4 min (Double Minor)</option>
                <option value={5}>5 min (Major)</option>
                <option value={10}>10 min (Misconduct)</option>
              </select>

              <select
                value={penalty.period}
                onChange={(e) =>
                  updatePenaltyEvent(index, "period", Number(e.target.value))
                }
                className={formClasses.select}
              >
                <option value={1}>Period 1</option>
                <option value={2}>Period 2</option>
                <option value={3}>Period 3</option>
              </select>

              <input
                type="text"
                value={penalty.time}
                onChange={(e) =>
                  updatePenaltyEvent(index, "time", e.target.value)
                }
                placeholder="MM:SS"
                className={formClasses.input}
              />

              <button
                onClick={() => removePenaltyEvent(index)}
                className={formClasses.deleteButton}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderScore = () => (
    <div>
      {/* Period by Period */}
      <div className={formClasses.teamSection}>
        <h4 className="text-lg font-semibold text-gray-800 mb-4">
          Period by Period
        </h4>
        <div className="w-full overflow-x-auto">
          <table className="w-full text-sm border-collapse mt-4 rounded-lg overflow-hidden shadow-sm">
            <thead>
              <tr>
                <th className="bg-gray-100 text-gray-800 font-semibold p-3 text-left border-b border-gray-200">Team</th>
                <th className="bg-gray-100 text-gray-800 font-semibold p-3 text-center border-b border-gray-200">1st</th>
                <th className="bg-gray-100 text-gray-800 font-semibold p-3 text-center border-b border-gray-200">2nd</th>
                <th className="bg-gray-100 text-gray-800 font-semibold p-3 text-center border-b border-gray-200">3rd</th>
                {localMatch.overtime.team1Goals > 0 ||
                  (localMatch.overtime.team2Goals > 0 && (
                    <th className="bg-gray-100 text-gray-800 font-semibold p-3 text-center border-b border-gray-200">OT</th>
                  ))}
                {localMatch.shootout.team1Goals > 0 ||
                  (localMatch.shootout.team2Goals > 0 && (
                    <th className="bg-gray-100 text-gray-800 font-semibold p-3 text-center border-b border-gray-200">SO</th>
                  ))}
                <th className="bg-gray-100 text-gray-800 font-semibold p-3 text-center border-b border-gray-200">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-3 border-b border-gray-200 bg-white font-medium">
                  {localMatch.team1Name || "Team 1"}
                </td>
                {localMatch.periods.map((period) => (
                  <td key={period.number} className="p-3 border-b border-gray-200 bg-white text-center">
                    {period.team1Goals}
                  </td>
                ))}
                {localMatch.overtime.team1Goals > 0 ||
                  (localMatch.overtime.team2Goals > 0 && (
                    <td className="p-3 border-b border-gray-200 bg-white text-center">{localMatch.overtime.team1Goals}</td>
                  ))}
                {localMatch.shootout.team1Goals > 0 ||
                  (localMatch.shootout.team2Goals > 0 && (
                    <td className="p-3 border-b border-gray-200 bg-white text-center">{localMatch.shootout.team1Goals}</td>
                  ))}
                <td className="p-3 border-b border-gray-200 bg-white text-center font-bold text-lg">
                  {localMatch.finalScore.team1}
                </td>
              </tr>
              <tr>
                <td className="p-3 border-b border-gray-200 bg-white font-medium">
                  {localMatch.team2Name || "Team 2"}
                </td>
                {localMatch.periods.map((period) => (
                  <td key={period.number} className="p-3 border-b border-gray-200 bg-white text-center">
                    {period.team2Goals}
                  </td>
                ))}
                {localMatch.overtime.team1Goals > 0 ||
                  (localMatch.overtime.team2Goals > 0 && (
                    <td className="p-3 border-b border-gray-200 bg-white text-center">{localMatch.overtime.team2Goals}</td>
                  ))}
                {localMatch.shootout.team1Goals > 0 ||
                  (localMatch.shootout.team2Goals > 0 && (
                    <td className="p-3 border-b border-gray-200 bg-white text-center">{localMatch.shootout.team2Goals}</td>
                  ))}
                <td className="p-3 border-b border-gray-200 bg-white text-center font-bold text-lg">
                  {localMatch.finalScore.team2}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Game Summary */}
      <div className="flex justify-between bg-white p-5 rounded-lg mt-5 shadow-sm flex-col md:flex-row gap-3">
        <div className="text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto">
          <p className="text-sm text-gray-500 mb-1">Total Goals</p>
          <p className="text-3xl font-bold text-gray-800">
            {localMatch.goalEvents.length}
          </p>
        </div>
        <div className="text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto">
          <p className="text-sm text-gray-500 mb-1">Total Penalties</p>
          <p className="text-3xl font-bold text-gray-800">
            {localMatch.penaltyEvents.length}
          </p>
        </div>
        <div className="text-center p-4 rounded-lg bg-blue-50 min-w-[120px]">
          <p className="text-sm text-gray-500 mb-1">Winner</p>
          <p className="text-base font-semibold text-blue-800">
            {localMatch.winner === "team1"
              ? localMatch.team1Name || "Team 1"
              : localMatch.winner === "team2"
              ? localMatch.team2Name || "Team 2"
              : "Tie Game"}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={formClasses.matchCard}>
      <div className={formClasses.matchHeader}>
        <h3 className={formClasses.matchTitle}>
          Hockey Match #{matchNumber}
        </h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Match
        </button>
      </div>

      <div className={formClasses.tabContainer}>
        {[
          { key: "basic", label: "Basic Info" },
          { key: "teams", label: "Teams" },
          { key: "events", label: "Goals & Penalties" },
          { key: "score", label: "Score Summary" },
        ].map((tab) => (
          <button
            key={tab.key}
            className={formClasses.tab}
            style={{
              ...tabBaseStyle,
              ...(activeTab === tab.key ? tabActiveStyle : {}),
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "basic" && renderBasicInfo()}
      {activeTab === "teams" && renderTeamManagement()}
      {activeTab === "events" && renderEvents()}
      {activeTab === "score" && renderScore()}
    </div>
  );
};

const PlayerSelector = ({
  availableUsers,
  onAddPlayer,
  selectedUserIds = [],
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("forward");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleAdd = () => {
    if (useCustomName && customName.trim()) {
      onAddPlayer(null, customName.trim(), selectedPosition);
      setCustomName("");
    } else if (!useCustomName && selectedUser) {
      onAddPlayer(selectedUser, "", selectedPosition);
      setSelectedUser(null);
      setSearchTerm("");
    }
  };

  const ps = {
    container: "flex flex-wrap gap-3 items-center relative",
    searchContainer: "relative flex-1 min-w-[250px]",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm",
    suggestionsDropdown:
      "absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-[200px] overflow-y-auto z-[1000] shadow-lg",
    suggestionItem:
      "px-3 py-2 border-b border-gray-100 transition-colors text-sm",
    button:
      "bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm",
    toggle: "text-blue-600 hover:text-blue-800 text-sm underline",
    noResults: "px-3 py-2 text-sm text-gray-600 text-center",
  };

  return (
    <div className={ps.container}>
      {!useCustomName ? (
        <>
          <div className={ps.searchContainer}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                // Delay to allow click on suggestion
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Search for system users..."
              className={ps.input}
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className={ps.suggestionsDropdown}>
                {suggestions.map((user) => {
                  const isAlreadySelected = selectedUserIds.includes(
                    user.id || user.user_id
                  );
                  return (
                    <div
                      key={user.id || user.user_id}
                      className={`${ps.suggestionItem} ${
                        isAlreadySelected
                          ? "opacity-50 cursor-not-allowed bg-gray-100"
                          : "cursor-pointer hover:bg-gray-50"
                      }`}
                      onClick={() =>
                        !isAlreadySelected && handleSelectUser(user)
                      }
                    >
                      <div className="font-medium">
                        {user.full_name || user.username}
                        {isAlreadySelected && (
                          <span className="text-red-500 ml-2">
                            (Already Added)
                          </span>
                        )}
                      </div>
                      {user.email && (
                        <div className="text-xs text-gray-600">
                          {user.email}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {showSuggestions &&
              searchTerm.length >= 2 &&
              suggestions.length === 0 && (
                <div className={ps.suggestionsDropdown}>
                  <div className={ps.noResults}>No users found</div>
                </div>
              )}
          </div>
          <button
            onClick={handleAdd}
            disabled={!selectedUser}
            className={`${ps.button} ${
              selectedUser ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            Add Player
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Enter custom player name"
            className={ps.input}
          />
          <button
            onClick={handleAdd}
            disabled={!customName.trim()}
            className={`${ps.button} ${
              customName.trim() ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            Add Player
          </button>
        </>
      )}

      <select
        value={selectedPosition}
        onChange={(e) => setSelectedPosition(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      >
        <option value="forward">Forward</option>
        <option value="defense">Defense</option>
        <option value="goalie">Goalie</option>
      </select>

      <span
        onClick={() => setUseCustomName(!useCustomName)}
        className={ps.toggle}
      >
        {useCustomName ? "Use system users" : "Add custom name"}
      </span>
    </div>
  );
};

export default AddHockey;
